import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../auth/AuthProvider';
import { useUiLanguageStore } from '../store/uiLanguageStore';
import { isLawyerClassRole } from '../router';
import { BRAND, MODULE_LIST } from '../theme';

// ─── Brand constants ─────────────────────────────────────────────────────────
const NAVY = '#0B2545';
const BRONZE = '#A47148';
const DIVIDER = 'rgba(11, 37, 69, 0.12)';

// ─── Editorial content — generic / client-agnostic ───────────────────────────
const PRACTICE_AREAS: ReadonlyArray<{ numeral: string; title: string; body: string }> = [
  {
    numeral: 'I',
    title: 'AI-Assisted Email Triage',
    body:
      'Inbound mail is classified, summarised bilingually (EN/AR), and routed in seconds. ' +
      'Internal correspondence is auto-recognised; client and external mail flow through structured extraction.',
  },
  {
    numeral: 'II',
    title: 'Matters & Retainers',
    body:
      'Setup matters, retainer contracts, deliverables, deadlines, and hour logs — tracked across ' +
      'multiple billing entities with phase-based progress and approval chains.',
  },
  {
    numeral: 'III',
    title: 'Client Lifecycle',
    body:
      'A single audited pipeline: inquiry, conflict check, KYC, proposal, retainer activation. ' +
      "Every step time-stamped; nothing falls between desks.",
  },
  {
    numeral: 'IV',
    title: 'Documents & Expiry',
    body:
      'POA, AOA, CR, contracts, and templates with bilingual OCR. ' +
      'Automated expiry alerts so corporate documents are never silently out of date.',
  },
  {
    numeral: 'V',
    title: 'Court & Hearings',
    body:
      'Hearing calendar, court correspondence triage, and case files — with multi-jurisdiction ' +
      'support across KSA, UAE, and partner jurisdictions.',
  },
  {
    numeral: 'VI',
    title: 'PDPL-Compliant by Design',
    body:
      'PII is scrubbed before any LLM dispatch. KSA PDPL and UAE Data Law obligations are wired ' +
      'into ingestion, retention, and erasure — not bolted on.',
  },
];

const WORKFLOWS: ReadonlyArray<{
  no: string;
  title: string;
  objective: string;
  steps: ReadonlyArray<{ label: string; detail: string }>;
}> = [
  {
    no: '01',
    title: 'Email → Action',
    objective:
      'Convert inbound correspondence into billable, accountable work — without a triage backlog or lost threads.',
    steps: [
      {
        label: 'Inbound mail received',
        detail:
          'Firm Gmail and Outlook accounts syndicate into the platform inbox. PII is redacted before any AI dispatch under the PDPL gate.',
      },
      {
        label: 'AI classification & summary',
        detail:
          'Mail is classified by intent, urgency, and matter affinity; summarised bilingually (EN/AR) with key facts and obligations extracted.',
      },
      {
        label: 'Routing & assignment',
        detail:
          'Routed to the responsible lawyer based on matter ownership, retainer scope, jurisdiction, and current workload.',
      },
      {
        label: 'Decision & response',
        detail:
          'Lawyer accepts, rejects, or escalates the thread. A recommended reply is drafted in tone; every decision is audit-logged.',
      },
      {
        label: 'Hours & invoice',
        detail:
          'Time is logged against the matter on accept; rolled into the next invoice run with a full audit trail per billing entity.',
      },
    ],
  },
  {
    no: '02',
    title: 'Inquiry → Engagement',
    objective:
      'Move from first contact to a signed retainer — with no manual handoffs, no missing checks, and a full audit log.',
    steps: [
      {
        label: 'Lead captured',
        detail:
          'Inbound contact recorded as a CRM lead with source, channel, jurisdiction, and initial scope of inquiry.',
      },
      {
        label: 'Conflict check',
        detail:
          'Cross-referenced against the existing client roster and adverse-party register. Flags raised for managing-partner review.',
      },
      {
        label: 'KYC & due diligence',
        detail:
          'Document collection (CR, POA, beneficial-owner IDs); ownership-chain verified; KYC packet assembled and stamped.',
      },
      {
        label: 'Proposal drafted',
        detail:
          'Scope of work and fee structure issued from the approved agreement-template library; partner-approved before despatch.',
      },
      {
        label: 'Retainer signed',
        detail:
          'Counter-signed retainer activates the billing entity; opening matter file is created automatically; lawyer assigned.',
      },
    ],
  },
  {
    no: '03',
    title: 'Setup → Renewal',
    objective:
      'Track every corporate vehicle from formation through the year-after-year compliance cycle — surfaced before it lapses.',
    steps: [
      {
        label: 'Formation',
        detail:
          'Entity registered (LLC, SPA, joint-venture, branch). Founders, capital structure, share-split, and signatories recorded.',
      },
      {
        label: 'Statutory filings',
        detail:
          'POA, AOA, and Commercial Registration drafted bilingually, executed, and lodged with the relevant registrar.',
      },
      {
        label: 'Activation',
        detail:
          'Bank account, VAT registration, and tax file linked to the matter; activation milestones tracked to completion.',
      },
      {
        label: 'Expiry tracking',
        detail:
          'CR / AOA / POA expiry dates surfaced 90, 60, and 30 days out — with named owners and escalation paths.',
      },
      {
        label: 'Renewal',
        detail:
          'Renewal cycle triggered automatically; documents re-issued; renewal invoice generated against the billing entity.',
      },
    ],
  },
];

const FACTS: ReadonlyArray<{ figure: string; label: string }> = [
  { figure: '11', label: 'Modules' },
  { figure: '14', label: 'Microservices' },
  { figure: 'EN/AR', label: 'Bilingual UI' },
  { figure: 'KSA · UAE', label: 'Jurisdictions' },
];

// ─── Inline decorations ──────────────────────────────────────────────────────

/** Bronze cartouche with the JY monogram — purely editorial. */
const SealMark: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 72 72"
    aria-hidden
    sx={{ width: { xs: 56, md: 72 }, height: { xs: 56, md: 72 }, flexShrink: 0 }}
  >
    <circle cx="36" cy="36" r="34" fill="none" stroke={BRONZE} strokeWidth="0.6" />
    <circle cx="36" cy="36" r="29" fill="none" stroke={BRONZE} strokeWidth="0.6" />
    <line x1="36" y1="3" x2="36" y2="9" stroke={BRONZE} strokeWidth="0.6" />
    <line x1="36" y1="63" x2="36" y2="69" stroke={BRONZE} strokeWidth="0.6" />
    <line x1="3" y1="36" x2="9" y2="36" stroke={BRONZE} strokeWidth="0.6" />
    <line x1="63" y1="36" x2="69" y2="36" stroke={BRONZE} strokeWidth="0.6" />
    <text
      x="36"
      y="44"
      textAnchor="middle"
      fontFamily="'Source Serif 4', Georgia, serif"
      fontSize="22"
      fontWeight={600}
      fill={NAVY}
      letterSpacing="-0.02em"
    >
      JY
    </text>
  </Box>
);

// ─── lowercase roman numerals for step indices (i, ii, iii…) ─────────────────
const lowerRoman = (n: number): string => {
  const map: ReadonlyArray<[number, string]> = [
    [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i'],
  ];
  let out = '';
  let x = n;
  for (const [v, s] of map) {
    while (x >= v) { out += s; x -= v; }
  }
  return out;
};

// ─── Section-header sub-component ────────────────────────────────────────────

const SectionHeader: React.FC<{ roman: string; kicker: string; title: string }> = ({
  roman,
  kicker,
  title,
}) => (
  <Box sx={{ mb: 3.5 }}>
    <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 0.75 }}>
      <Typography
        component="span"
        sx={{
          fontFamily: '"Source Serif 4", Georgia, serif',
          fontStyle: 'italic',
          fontSize: '1rem',
          color: 'secondary.main',
          fontWeight: 500,
        }}
      >
        {roman}
      </Typography>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          letterSpacing: '0.18em',
          fontSize: '0.6875rem',
          fontFamily: '"JetBrains Mono", "Menlo", monospace',
        }}
      >
        {kicker}
      </Typography>
    </Stack>
    <Typography
      component="h2"
      sx={{
        fontFamily: '"Source Serif 4", Georgia, serif',
        fontSize: { xs: '1.625rem', md: '2rem' },
        fontWeight: 600,
        color: 'primary.main',
        lineHeight: 1.15,
        letterSpacing: '-0.01em',
        mb: 1,
      }}
    >
      {title}
    </Typography>
    <Box sx={{ width: 48, height: '1.5px', bgcolor: 'secondary.main' }} />
  </Box>
);

// ─── Main component ──────────────────────────────────────────────────────────

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading, error } = useAuth();
  const { t } = useTranslation('common');
  const { locale, toggle } = useUiLanguageStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const fromPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { user } = await login({ username, password }, { expectedAud: 'jylaw_internal' });
      if (fromPath && fromPath !== '/login') {
        navigate(fromPath, { replace: true });
      } else if (user?.role === 'client_portal_user') {
        navigate('/client');
      } else if (isLawyerClassRole(user?.role)) {
        navigate('/me');
      } else if (user?.role === 'managing_partner') {
        navigate('/dashboard/ops', { replace: true });
      } else {
        navigate('/');
      }
    } catch {
      /* error surfaced via useAuth().error */
    }
  };

  const otherLang = locale === 'ar' ? t('language.english') : t('language.arabic');
  const dateStamp = new Date().toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        backgroundImage:
          'radial-gradient(rgba(11, 37, 69, 0.022) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        '@keyframes jyEnter': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        animation: 'jyEnter 500ms ease-out both',
      }}
    >
      {/* ── Masthead bar (newspaper-style meta strip) ──────────────────────── */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Container maxWidth="lg" sx={{ py: 1.25 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography
              component="span"
              sx={{
                fontFamily: '"JetBrains Mono", "Menlo", monospace',
                color: 'text.secondary',
                letterSpacing: '0.14em',
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
              }}
            >
              JY Law · Legal Operations · {dateStamp}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                component="span"
                sx={{
                  display: { xs: 'none', md: 'inline' },
                  fontFamily: '"JetBrains Mono", "Menlo", monospace',
                  color: 'text.secondary',
                  letterSpacing: '0.14em',
                  fontSize: '0.6875rem',
                  textTransform: 'uppercase',
                }}
              >
                Vol. I · No. 1
              </Typography>
              <Tooltip title={t('language.switchTo', { lang: otherLang })}>
                <IconButton
                  aria-label="toggle language"
                  onClick={toggle}
                  size="small"
                  sx={{ color: 'primary.main', borderRadius: 0 }}
                >
                  <LanguageIcon fontSize="small" />
                  <Typography
                    component="span"
                    sx={{
                      ml: 0.5,
                      fontFamily: '"JetBrains Mono", "Menlo", monospace',
                      fontSize: '0.6875rem',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {locale === 'ar' ? 'EN' : 'AR'}
                  </Typography>
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Box>
      {/* The bronze hairline — the editorial accent across the whole page */}
      <Box sx={{ height: '2px', bgcolor: 'secondary.main' }} />

      {/* ── Body — single full-width editorial column ─────────────────────── */}
      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 6, md: 8 } }}>
        {/* Hero */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          spacing={{ xs: 2, sm: 4 }}
          sx={{ mb: 1.5 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Source Serif 4", "Source Serif Pro", Georgia, serif',
                fontWeight: 600,
                fontSize: { xs: '3.5rem', sm: '5rem', md: '7rem' },
                lineHeight: 0.92,
                letterSpacing: '-0.025em',
                color: 'primary.main',
                mb: 0.5,
              }}
            >
              {BRAND.displayName}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: '"Noto Naskh Arabic", "IBM Plex Sans Arabic", "Tajawal", serif',
                fontSize: { xs: '1.375rem', md: '1.75rem' },
                color: 'secondary.main',
                fontWeight: 500,
                display: 'inline-block',
              }}
              dir="rtl"
            >
              {BRAND.displayNameAr}
            </Typography>
          </Box>
          <Box sx={{ alignSelf: { xs: 'flex-end', sm: 'flex-start' } }}>
            <SealMark />
          </Box>
        </Stack>

        {/* Tagline + bronze stroke */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 3.5, mb: 4 }}>
          <Box sx={{ width: 64, height: '1px', bgcolor: 'secondary.main' }} />
          <Typography
            variant="overline"
            sx={{
              color: 'text.primary',
              letterSpacing: '0.18em',
              fontSize: '0.8125rem',
              fontWeight: 500,
            }}
          >
            Operations platform · MENA legal market
          </Typography>
        </Stack>

        {/* Lead paragraph — with drop cap */}
        <Typography
          sx={{
            fontFamily: '"Source Serif 4", "Source Serif Pro", Georgia, serif',
            fontSize: { xs: '1.1875rem', md: '1.375rem' },
            lineHeight: 1.55,
            color: 'text.primary',
            mb: 5,
            maxWidth: 820,
            '&::first-letter': {
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontSize: '3.5em',
              fontWeight: 700,
              float: 'left',
              lineHeight: 0.85,
              paddingRight: '0.1em',
              paddingTop: '0.06em',
              color: 'secondary.main',
            },
          }}
        >
          An operations platform built for law firms practicing across KSA, the UAE, and the wider
          Arab region — combining AI-assisted email triage, matter management, client KYC, court
          tracking, bilingual documents, and multi-entity billing under one disciplined system of
          record.
        </Typography>

        {/* Facts strip — column-ruled like a financial page */}
        <Box
          sx={{
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 2.75,
            mb: 8,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            rowGap: 2,
            columnGap: 0,
          }}
        >
          {FACTS.map((fact, idx) => (
            <Box
              key={fact.label}
              sx={{
                px: { xs: 1, sm: 3 },
                borderInlineStart:
                  idx === 0
                    ? 'none'
                    : {
                        xs: idx === 2 ? 'none' : `1px solid ${DIVIDER}`,
                        sm: `1px solid ${DIVIDER}`,
                      },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Source Serif 4", Georgia, serif',
                  fontWeight: 600,
                  fontSize: { xs: '1.625rem', md: '2.125rem' },
                  color: 'primary.main',
                  lineHeight: 1.05,
                  letterSpacing: '-0.01em',
                }}
              >
                {fact.figure}
              </Typography>
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  letterSpacing: '0.12em',
                  fontSize: '0.6875rem',
                  fontFamily: '"JetBrains Mono", "Menlo", monospace',
                }}
              >
                {fact.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── I. Practice Areas ───────────────────────────────────────────── */}
        <SectionHeader roman="I." kicker="What it does" title="Practice areas served" />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
            borderTop: '1px solid',
            borderColor: 'divider',
            mb: 8,
          }}
        >
          {PRACTICE_AREAS.map((area, idx) => {
            const colCount = { xs: 1, sm: 2, md: 3 };
            return (
              <Box
                key={area.title}
                sx={{
                  py: 3,
                  px: { xs: 0, sm: 2.5 },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  borderInlineStart: {
                    xs: 'none',
                    sm: idx % colCount.sm !== 0 ? `1px solid ${DIVIDER}` : 'none',
                    md: idx % colCount.md !== 0 ? `1px solid ${DIVIDER}` : 'none',
                  },
                  position: 'relative',
                  transition: 'background-color 220ms ease',
                  '&:hover': { bgcolor: 'rgba(164, 113, 72, 0.035)' },
                  '&:hover .roman': { color: 'primary.main' },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1 }}>
                  <Typography
                    className="roman"
                    component="span"
                    sx={{
                      fontFamily: '"Source Serif 4", Georgia, serif',
                      fontStyle: 'italic',
                      fontSize: '1.625rem',
                      color: 'secondary.main',
                      fontWeight: 500,
                      minWidth: '2.4ch',
                      transition: 'color 220ms ease',
                      lineHeight: 1,
                    }}
                  >
                    {area.numeral}
                  </Typography>
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: '"Source Serif 4", Georgia, serif',
                      fontSize: '1.0625rem',
                      fontWeight: 600,
                      color: 'text.primary',
                      lineHeight: 1.3,
                    }}
                  >
                    {area.title}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    lineHeight: 1.55,
                    color: 'text.secondary',
                    pl: '3.4ch',
                  }}
                >
                  {area.body}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* ── II. Operating workflows (reworked: proper detailed flows) ──── */}
        <SectionHeader roman="II." kicker="How work moves" title="Operating workflows" />
        <Stack spacing={6} sx={{ mb: 8 }}>
          {WORKFLOWS.map((wf, wfIdx) => (
            <Box
              key={wf.title}
              sx={{
                pb: wfIdx === WORKFLOWS.length - 1 ? 0 : 5,
                borderBottom: wfIdx === WORKFLOWS.length - 1 ? 'none' : '1px solid',
                borderColor: 'divider',
              }}
            >
              {/* Workflow header — number + title */}
              <Stack direction="row" spacing={2.5} alignItems="baseline" sx={{ mb: 1.25 }}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: '"JetBrains Mono", "Menlo", monospace',
                    fontSize: '0.9375rem',
                    color: 'secondary.main',
                    fontWeight: 500,
                    letterSpacing: '0.06em',
                  }}
                >
                  {wf.no}
                </Typography>
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: '"Source Serif 4", Georgia, serif',
                    fontWeight: 600,
                    fontSize: { xs: '1.375rem', md: '1.625rem' },
                    color: 'primary.main',
                    lineHeight: 1.2,
                    letterSpacing: '-0.005em',
                  }}
                >
                  {wf.title}
                </Typography>
              </Stack>

              {/* Objective */}
              <Typography
                sx={{
                  fontFamily: '"Source Serif 4", Georgia, serif',
                  fontStyle: 'italic',
                  fontSize: { xs: '1rem', md: '1.0625rem' },
                  color: 'text.secondary',
                  mb: 3.5,
                  maxWidth: 780,
                  lineHeight: 1.55,
                  pl: { xs: 0, md: '4.5ch' },
                }}
              >
                {wf.objective}
              </Typography>

              {/* Steps — vertical column with hairline rail */}
              <Box
                sx={{
                  ml: { xs: 0, md: '3.5ch' },
                  pl: { xs: 2.5, md: 3.5 },
                  borderInlineStart: `1px solid ${DIVIDER}`,
                }}
              >
                {wf.steps.map((step, i) => (
                  <Box
                    key={step.label}
                    sx={{
                      position: 'relative',
                      py: 2,
                      borderBottom: i === wf.steps.length - 1 ? 'none' : `1px dashed ${DIVIDER}`,
                      // Bronze step marker on the hairline rail
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        insetInlineStart: { xs: '-22px', md: '-30px' },
                        top: '1.85em',
                        width: 8,
                        height: 8,
                        bgcolor: BRONZE,
                        borderRadius: '50%',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="baseline">
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: '"Source Serif 4", Georgia, serif',
                          fontStyle: 'italic',
                          color: 'secondary.main',
                          fontSize: '0.9375rem',
                          fontWeight: 500,
                          minWidth: '3.4ch',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {lowerRoman(i + 1)}.
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: '"Source Serif 4", Georgia, serif',
                            fontWeight: 600,
                            fontSize: { xs: '1rem', md: '1.0625rem' },
                            color: 'text.primary',
                            lineHeight: 1.35,
                            mb: 0.5,
                          }}
                        >
                          {step.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.9rem',
                            color: 'text.secondary',
                            lineHeight: 1.55,
                          }}
                        >
                          {step.detail}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>

        {/* ── III. Modules ────────────────────────────────────────────────── */}
        <SectionHeader roman="III." kicker="Surface area" title="Modules" />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
            columnGap: { sm: 4, md: 5 },
            rowGap: 0,
            mb: 9,
          }}
        >
          {MODULE_LIST.map((m) => (
            <Stack
              key={m.id}
              direction="row"
              spacing={1.5}
              alignItems="baseline"
              sx={{
                py: 1.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
                transition: 'border-color 220ms ease',
                '&:hover': { borderBottomColor: m.accent },
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  mt: '0.55em',
                  bgcolor: m.accent,
                  borderRadius: '50%',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: '"Source Serif 4", Georgia, serif',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    color: 'text.primary',
                    lineHeight: 1.3,
                  }}
                >
                  {m.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    lineHeight: 1.45,
                  }}
                >
                  {m.description}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>

        {/* ── IV. Access — Counsel + Client side by side at the bottom ───── */}
        <SectionHeader roman="IV." kicker="Sign in" title="Access" />
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {/* ─── Counsel Access — internal workspace login form ─────────── */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                bgcolor: 'background.paper',
                borderColor: 'surfaceBorder',
                borderRadius: 0,
                position: 'relative',
                height: '100%',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  insetInlineStart: 0,
                  width: 64,
                  height: '2px',
                  bgcolor: 'secondary.main',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'secondary.main',
                    letterSpacing: '0.2em',
                    fontFamily: '"JetBrains Mono", "Menlo", monospace',
                    fontSize: '0.6875rem',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Counsel Access
                </Typography>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: '"Source Serif 4", Georgia, serif',
                    fontSize: '1.75rem',
                    fontWeight: 600,
                    color: 'primary.main',
                    lineHeight: 1.1,
                    letterSpacing: '-0.005em',
                    mb: 1,
                  }}
                >
                  {t('auth.login')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    mb: 3,
                    lineHeight: 1.55,
                  }}
                >
                  Internal workspace for partners, lawyers, and operations.
                </Typography>

                {error && (
                  <Alert
                    severity="error"
                    variant="outlined"
                    sx={{ mb: 2, borderRadius: 0, fontSize: '0.8125rem' }}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <TextField
                    label={t('auth.username')}
                    fullWidth
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    sx={{ mb: 2 }}
                    id="login-username"
                    name="username"
                    autoComplete="username"
                    variant="outlined"
                  />
                  <TextField
                    label={t('auth.password')}
                    type="password"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ mb: 3 }}
                    id="login-password"
                    name="password"
                    autoComplete="current-password"
                    variant="outlined"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={loading}
                    size="large"
                    sx={{
                      borderRadius: 0,
                      py: 1.5,
                      fontFamily: '"Inter", "Roboto", sans-serif',
                      fontWeight: 500,
                      letterSpacing: '0.06em',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                    }}
                  >
                    {loading ? t('common.loading') : t('auth.login')}
                  </Button>
                </Box>

                <Divider sx={{ my: 3, borderColor: 'divider' }} />

                <Stack spacing={0.75}>
                  {['Bilingual EN / AR', 'KSA + UAE jurisdictions', 'PDPL compliant'].map((line) => (
                    <Stack key={line} direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 4, height: 4, bgcolor: 'secondary.main', flexShrink: 0 }} />
                      <Typography
                        sx={{
                          color: 'text.secondary',
                          fontFamily: '"JetBrains Mono", "Menlo", monospace',
                          fontSize: '0.6875rem',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {line}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* ─── Client Access — gateway to the client portal ──────────── */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                bgcolor: 'background.paper',
                borderColor: 'surfaceBorder',
                borderRadius: 0,
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  insetInlineEnd: 0,
                  width: 64,
                  height: '2px',
                  bgcolor: 'primary.main',
                },
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 3, md: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: 'primary.main',
                    letterSpacing: '0.2em',
                    fontFamily: '"JetBrains Mono", "Menlo", monospace',
                    fontSize: '0.6875rem',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Client Access
                </Typography>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: '"Source Serif 4", Georgia, serif',
                    fontSize: '1.75rem',
                    fontWeight: 600,
                    color: 'primary.main',
                    lineHeight: 1.1,
                    letterSpacing: '-0.005em',
                    mb: 1,
                  }}
                >
                  Client portal
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    mb: 3,
                    lineHeight: 1.55,
                  }}
                >
                  For clients with an active engagement — view your matters, retainers, invoices,
                  and documents in a dedicated, role-scoped portal.
                </Typography>

                {/* What's inside the client portal — short bilingual feature list */}
                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  {[
                    { en: 'Matters & retainers', ar: 'الملفات والاتفاقيات' },
                    { en: 'Invoices & payment history', ar: 'الفواتير وسجل المدفوعات' },
                    { en: 'Document library & KYC uploads', ar: 'مكتبة الوثائق ورفع وثائق العميل' },
                    { en: 'Direct counsel correspondence', ar: 'التواصل المباشر مع المستشار' },
                  ].map((item) => (
                    <Stack
                      key={item.en}
                      direction="row"
                      spacing={1.5}
                      alignItems="baseline"
                      sx={{ pb: 1.25, borderBottom: `1px solid ${DIVIDER}` }}
                    >
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          mt: '0.55em',
                          bgcolor: 'primary.main',
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: '"Source Serif 4", Georgia, serif',
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            color: 'text.primary',
                            lineHeight: 1.3,
                          }}
                        >
                          {item.en}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Noto Naskh Arabic", "Tajawal", serif',
                            fontSize: '0.875rem',
                            color: 'text.secondary',
                            lineHeight: 1.4,
                          }}
                          dir="rtl"
                        >
                          {item.ar}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>

                <Box sx={{ mt: 'auto' }}>
                  <Button
                    onClick={() => navigate('/client/login')}
                    variant="outlined"
                    color="primary"
                    fullWidth
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      borderRadius: 0,
                      py: 1.5,
                      fontFamily: '"Inter", "Roboto", sans-serif',
                      fontWeight: 500,
                      letterSpacing: '0.06em',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      borderWidth: '1.5px',
                      '&:hover': { borderWidth: '1.5px', bgcolor: 'rgba(11, 37, 69, 0.04)' },
                    }}
                  >
                    Open client portal
                  </Button>
                  <Typography
                    sx={{
                      mt: 1.5,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      fontStyle: 'italic',
                      fontFamily: '"Source Serif 4", Georgia, serif',
                    }}
                  >
                    Need an account? Speak to your counsel — client accounts are issued on
                    engagement, not by self-signup.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Editorial pull-quote under the access cards ─────────────────── */}
        <Box
          sx={{
            mt: 5,
            pl: 3,
            py: 1,
            borderInlineStart: `2px solid ${BRONZE}`,
            maxWidth: 760,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontStyle: 'italic',
              fontSize: { xs: '1rem', md: '1.0625rem' },
              color: 'text.primary',
              lineHeight: 1.55,
              mb: 1,
            }}
          >
            "Hairline rules over drop shadows. Off-white parchment over pure white. Density that
            respects the lawyer's hour."
          </Typography>
          <Typography
            component="span"
            sx={{
              color: 'text.secondary',
              fontFamily: '"JetBrains Mono", "Menlo", monospace',
              fontSize: '0.6875rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            — Platform design ethos
          </Typography>
        </Box>
      </Container>

      {/* ── Footer hairline (colophon strip) ──────────────────────────────── */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
          >
            <Typography
              component="span"
              sx={{
                color: 'text.secondary',
                fontFamily: '"JetBrains Mono", "Menlo", monospace',
                fontSize: '0.625rem',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
              }}
            >
              {BRAND.displayName} · Legal Operations · MENA · {new Date().getFullYear()}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: 'text.secondary',
                fontFamily: '"JetBrains Mono", "Menlo", monospace',
                fontSize: '0.625rem',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
              }}
            >
              {locale === 'ar' ? 'AR · ' : 'EN · '}Build 1.0 · Set in Source Serif &amp; Inter
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default LoginPage;
